<template>
  <div ref="blockRef" class="relative mx-auto max-w-[120rem]">
    <div class="mx-auto max-w-6xl md:py-12">
      <div class="px-4 md:px-6 lg:relative lg:z-[1] lg:max-w-[60%]">
        <h1
          class="text-purple-dark font-serif font-bold text-[clamp(1.5rem,-3.1753rem+23.3766vw,6rem)] leading-none"
          data-gsap-target="title"
        >
          {{ entry.title }}
        </h1>

        <div v-if="entry.vacancy_categories?.length" class="flex items-center flex-wrap gap-2 mt-4">
          <div
            v-for="category in entry.vacancy_categories"
            :key="category.id"
            class="btn bg-purple-light text-purple-dark"
            data-gsap-target="animate"
          >
            {{ category.title }}
          </div>
        </div>

        <div class="mt-12 max-w-md">
          <div class="flex flex-wrap gap-6 text-purple-dark md:text-lg">
            <div v-if="entry.hours_per_week" class="flex items-center" data-gsap-target="animate">
              <Icon icon="svgo-clock" class="mr-2 h-5 w-5" /> {{ entry.hours_per_week }}
            </div>

            <div v-if="entry.location" class="flex items-center" data-gsap-target="animate">
              <Icon icon="svgo-pin" class="mr-2 h-5 w-5" /> {{ entry.location }}
            </div>
          </div>

          <div
            v-if="entry.text"
            class="prose mt-6 text-purple-dark md:mt-8"
            v-html="entry.text"
            data-gsap-target="animate"
          />

          <div class="mt-6 md:mt-12">
            <Button
              :text="$statamic.trans('apply_now') as string"
              link="@overlay"
              variant="btn--primary"
              data-gsap-target="animate"
            />
          </div>
        </div>
      </div>

      <div
        v-if="entry.image"
        class="mt-6 lg:absolute lg:top-0 lg:right-0 lg:bottom-0 lg:mt-0 lg:max-w-[50%]"
        data-gsap-target="image"
      >
        <img
          :src="entry.image?.md.url"
          :srcSet="`
            ${entry.image?.xl.url} ${entry.image?.xl.width}w,
            ${entry.image?.lg.url} ${entry.image?.lg.width}w,
            ${entry.image?.md.url} ${entry.image?.md.width}w
          `"
          :alt="entry.image?.md.alt"
          class="h-full w-full object-cover aspect-[5/4] lg:rounded-l-5xl [@media(min-width:1920px)]:rounded-r-5xl"
          sizes="
            (min-width: 1920px) 912px,
            (min-width: 768px) 50vw,
            100vw
          "
          :width="entry.image?.md.width"
          :height="entry.image?.md.height"
          loading="lazy"
          draggable="false"
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Entry } from '~/statamic/types/entry';

defineProps<{
  entry: Entry;
}>();

let ctx: gsap.Context | null;
const blockRef = ref();

onMounted(() => {
  ctx = useGsap.context((self) => {
    if (typeof self.selector !== 'function') return;

    const title = self.selector('[data-gsap-target="title"]');
    const image = self.selector('[data-gsap-target="image"]');
    const animations = self.selector('[data-gsap-target="animate"]');

    const timeline = useGsap.timeline({
      scrollTrigger: {
        start: 'top bottom',
        trigger: blockRef.value,
      },
    });

    if (title?.length) {
      timeline.from(title, {
        x: 100,
        autoAlpha: 0,
      });
    }

    if (animations?.length) {
      timeline.from(animations, {
        y: 100,
        x: 100,
        stagger: 0.25,
        autoAlpha: 0,
      });
    }

    if (image?.length) {
      timeline.from(image, {
        x: 100,
        autoAlpha: 0,
      });
    }
  }, blockRef.value);
});

onUnmounted(() => {
  ctx?.revert();
});
</script>
